import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { DialogId } from "./dialog_id.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";

export class DisconnectAccountNotification extends AbstractDialog
{
  public static readonly dialog_id = DialogId.DISCONNECT_ACCOUNT_NOTIFICATION_DIALOG;

  private bg!: RoundedRectangle;
  private content_bg!: RoundedRectangle;

  constructor( private readonly on_ok?: () => void )
  {
    const body_w_design = 990;
    const body_h_design = 706;

    const body_w = LayoutScaler.widthFromDesignAdaptive( body_w_design );
    const body_h = LayoutScaler.heightFromDesign( body_h_design );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, false );

    this.createBackground();
    this.createTitle();
    this.createContentText();
    this.createCancelButton();
  }

  public name(): string { return "DisconnectAccountNotification"; }

  public getContentBg(): RoundedRectangle
  {
    return this.content_bg;
  }

  private createBackground(): void
  {
    const bg_color = "#FFFFFF";
    const bg_corner_radius_design = { tl: 30, tr: 30, br: 70, bl: 70 };

    const circle_w_design = 237;
    const circle_h_design = 234;
    const circle_radius_design = 117;
    const circle_y_design = 136;

    const content_w_design = 948;
    const content_h_design = 376;
    const content_top_design = 134;

    const circle_w = LayoutScaler.widthFromDesign( circle_w_design );
    const circle_h = LayoutScaler.heightFromDesign( circle_h_design );
    const r = LayoutScaler.cornerRadiusFromDesign( circle_radius_design );
    const circle_y = LayoutScaler.yFromDesign( circle_y_design );

    const content_w = LayoutScaler.widthFromDesign( content_w_design );
    const content_h = LayoutScaler.heightFromDesign( content_h_design );
    const content_y = LayoutScaler.yFromDesign( content_top_design );

    this.bg = new RoundedRectangle(
      this.ctx,
      0, 0,
      this.rect.getW(),
      this.rect.getH(),
      {
        tl: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.tl ),
        tr: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.tr ),
        br: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.br ),
        bl: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.bl )
      },
      bg_color
    );
    this.addChild( this.bg );

    const circle_x = this.rect.getW2() - circle_w / 2;

    const circle = new RoundedRectangle(
      this.ctx,
      circle_x,
      -circle_y,
      circle_w,
      circle_h,
      r,
      bg_color
    );
    this.addChild( circle );

    const content_x = this.rect.getW2() - content_w / 2;

    this.content_bg = new RoundedRectangle(
      this.ctx,
      content_x,
      content_y,
      content_w,
      content_h,
      0,
      "#FFF3E8"
    );
    this.bg.addChild( this.content_bg );
  }

  private createTitle(): void
  {
    const title_w = this.rect.getW();
    const title_h_design = 68;
    const title_y_design = 49;
    const title_h = LayoutScaler.heightFromDesign( title_h_design );
    const title_y = LayoutScaler.yFromDesign( title_y_design );
    const title_font_size_design = 50;

    const title = new UITextElement(
      this.ctx,
      0,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "DISCONNECT_NOTIFICATION_DIALOG.TITLE",
        text_color: "#DE934F",
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( title_font_size_design ),
        font_weight: 700,
        auto_wrap: false,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    this.addChild( title );
  }

  private createContentText(): void
  {
    const cw = this.content_bg.getRect().getW();
    const ch = this.content_bg.getRect().getH();
    const content_text_w_design = 888;
    const content_text_h_design = 327;
    const text_font_size_design = 40;

    const text_w = LayoutScaler.widthFromDesign( content_text_w_design );
    const text_h = LayoutScaler.heightFromDesign( content_text_h_design );
    const text_x = ( cw - text_w ) / 2;
    const text_y = ( ch - text_h ) / 2;

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        localization_key: "DISCONNECT_NOTIFICATION_DIALOG.TEXT",
        text_color: "#A85F1E",
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( text_font_size_design ),
        font_weight: 700,
        auto_wrap: true,
        using_y_offset: false,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    this.content_bg.addChild( text );
  }

  private createCancelButton(): void
  {
    const btn_w_design = 466;
    const btn_h_design = 112;
    const btn_radius_design = 56;
    const btn_y_design = 556;
    const btn_font_size_design = 48;

    const btn_w = LayoutScaler.widthFromDesign( btn_w_design );
    const btn_h = LayoutScaler.heightFromDesign( btn_h_design );
    const r = LayoutScaler.cornerRadiusFromDesign( btn_radius_design );
    const btn_y = LayoutScaler.yFromDesign( btn_y_design );
    const btn_x = this.rect.getW2() - btn_w / 2;

    const ok_btn = new RoundedRectangle(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      r,
      "#12B2E2",
      undefined,
      true
    );
    ok_btn.setOnClick( () =>
    {
      DialogManager.closeDialog();
      this.on_ok?.();
    } );
    ok_btn.addChild( new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "OK",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( btn_font_size_design ),
        font_weight: 700,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_light_blue_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    ), true );
    this.addChild( ok_btn );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
